import { getFormattedDateDistance } from 'modules/predictiveGoals/vocabUtils';
import { intersection } from 'fp-ts/Array';
import * as N from 'fp-ts/number';
import { pipe } from 'fp-ts/function';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { Member } from '../../types';
import { fullName } from '../../utils';
import { MemberRow } from './types';
import {
  Filters,
  ORG_ADMIN,
  ORG_MEMBER,
  MFA_DISABLED,
  MFA_ENABLED,
  PROJECT_MEMBERSHIP,
} from '../../contexts/FiltersContext';
import {
  FULL_NAME,
  ORGANIZATION_ROLE,
  PROJECT_MEMBERSHIPS,
  TFA,
  LAST_LOGIN_AT,
  CREATED_AT,
  TABLE_GRID_7_COLUMNS,
  TABLE_GRID_8_COLUMNS,
} from './constants';

export const createMemberRow = (member: Member): MemberRow => ({
  ...member,
  id: member.userId,
  fullName: fullName(member),
  organizationRole: member.orgAdmin ? 'Admin' : 'Member',
  projectMemberships: member.projectMemberships || [],
  projectMembershipCount: member.orgAdmin ? Infinity : member.projectMemberships?.length || 0,
  projectIds: member.projectMemberships?.map((membership) => membership.projectId) || [],
  organizationPermissions: member.organizationPermissions || [],
});

export const matchesSearch = (row: MemberRow, search: string) => {
  const searchLowerCase = search.toLowerCase();
  return (
    fullName(row).toLowerCase().includes(searchLowerCase) ||
    row.email.toLowerCase().includes(searchLowerCase)
  );
};

export const getGridLayout = (isMfaAvailable: boolean) =>
  isMfaAvailable ? TABLE_GRID_8_COLUMNS : TABLE_GRID_7_COLUMNS;

export const matchesProjects = (row: MemberRow, byProjectId: Set<number>) =>
  pipe([...byProjectId.keys()], intersection(N.Eq)(row.projectIds), (ids) => ids.length > 0);

export const filterMemberRow = (row: MemberRow, search: string, filters: Filters) => {
  if (search && !matchesSearch(row, search)) return false;

  if (filters[ORG_ADMIN] && !row.orgAdmin) return false;
  if (filters[ORG_MEMBER] && row.orgAdmin) return false;

  if (filters[MFA_ENABLED] && !row.tfa) return false;
  if (filters[MFA_DISABLED] && row.tfa) return false;

  // Project membership match is most expensive, only run if the others haven't already returned
  if (filters[PROJECT_MEMBERSHIP].size && !matchesProjects(row, filters[PROJECT_MEMBERSHIP]))
    return false;

  return true;
};

export const recencyReadable = (timestamp: number) =>
  getFormattedDateDistance({ laterDate: new Date(), earlierDate: new Date(timestamp) });

export const compareString = (a: string, b: string, ascending = false) => {
  if (a === b) return 0;
  return ascending ? b.localeCompare(a) : a.localeCompare(b);
};

export const compareTimestamp = (timestampA?: number, timestampB?: number, ascending = false) => {
  const valA = timestampA || 0;
  const valB = timestampB || 0;

  return ascending ? valA - valB : valB - valA;
};

export const compareBoolean = (boolA: boolean, boolB: boolean, ascending = false) => {
  if (boolA === boolB) return 0;
  if (boolA === true) return ascending ? 1 : -1;
  return ascending ? -1 : 1;
};

export const compareNumber = (a: number, b: number, ascending = false) => {
  if (a === b) return 0;
  return ascending ? a - b : b - a;
};

export const compareMemberColumns =
  (sortBy: keyof MemberRow, sortDirection: SORT_DIRECTIONS) =>
  (rowA: MemberRow, rowB: MemberRow) => {
    const valueA = rowA[sortBy];
    const valueB = rowB[sortBy];

    if (sortBy === FULL_NAME || sortBy === ORGANIZATION_ROLE) {
      return compareString(
        valueA as string,
        valueB as string,
        sortDirection === SORT_DIRECTIONS.ASCENDING,
      );
    }

    if (sortBy === LAST_LOGIN_AT || sortBy === CREATED_AT) {
      return compareTimestamp(
        (valueA as number) || 0,
        (valueB as number) || 0,
        sortDirection === SORT_DIRECTIONS.ASCENDING,
      );
    }

    if (sortBy === TFA) {
      return compareBoolean(
        valueA as boolean,
        valueB as boolean,
        sortDirection === SORT_DIRECTIONS.ASCENDING,
      );
    }

    if (sortBy === PROJECT_MEMBERSHIPS) {
      return compareNumber(
        rowA.projectMembershipCount,
        rowB.projectMembershipCount,
        sortDirection === SORT_DIRECTIONS.ASCENDING,
      );
    }

    return 0;
  };
